import type { ComponentType, MouseEvent as ReactMouseEvent } from 'react';
import { forwardRef, useId } from 'react';
// Three bespoke multi-slot skins (see the classNames maps below) rebuild Radio as a card, a pill
// row, and a large card — that's `classNames`, which the core-ui `Radio` locks, and it's a wrapper
// redesign rather than a call-site swap. TODO(EPD-1264-followup): move these skins into theme
// variants and drop back to `@highlight-cp/core-ui`.
// oxlint-disable-next-line no-restricted-imports
import { Radio as MantineRadio } from '@mantine/core';
// oxlint-disable-next-line no-restricted-imports
import type { RadioProps as MantineRadioProps } from '@mantine/core';

import { Box, Flex, Stack, Text } from '@highlight-cp/core-ui';

import classes from './guided-step-radio-button.module.css';

/** Pixel size passed to icon components (e.g. react-icons `size`). */
const GUIDED_STEP_RADIO_BUTTON_ICON_SIZE = 22;

/**
 * Mantine puts padding/gap on `root`/`body`, which are plain divs — only the `<input>` and `<label>`
 * activate the radio. Delegate clicks from those dead zones when row layout (`isRow`) is enabled.
 */
function handleGuidedStepRadioRootClick(event: ReactMouseEvent<HTMLElement>) {
  const root = event.currentTarget;
  const input = root.querySelector<HTMLInputElement>('input[type="radio"]');
  if (!input || input.disabled) return;

  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (target === input || input.contains(target)) return;
  if (target.closest('label')) return;

  input.click();
}

/**
 * Props for {@link GuidedStepRadioButton} and {@link GuidedStepRadioButton.Row}.
 * Row layout (visible radio circle) is selected via `<GuidedStepRadioButton.Row>` only.
 */
export type GuidedStepRadioButtonProps = Omit<
  MantineRadioProps,
  | 'className'
  | 'classNames'
  | 'color'
  | 'description'
  | 'icon'
  | 'label'
  | 'labelPosition'
  | 'radius'
  | 'size'
  | 'style'
  | 'styles'
  | 'sx'
  | 'unstyled'
  | 'variant'
  | 'withAsterisk'
> & {
  /** Primary line (label). */
  label: string;
  /**
   * Optional leading icon component (e.g. from `react-icons`). Rendered at a fixed size inside
   * the control. The slot is `aria-hidden`; use for decorative icons only—meaning must be conveyed
   * by `label`.
   */
  icon?: ComponentType<{ size?: number }>;
};

/**
 * Props for {@link GuidedStepRadioButton.Large}.
 */
export type GuidedStepRadioButtonLargeProps = Omit<
  MantineRadioProps,
  | 'className'
  | 'classNames'
  | 'color'
  | 'description'
  | 'icon'
  | 'label'
  | 'labelPosition'
  | 'radius'
  | 'size'
  | 'style'
  | 'styles'
  | 'sx'
  | 'unstyled'
  | 'variant'
  | 'withAsterisk'
> & {
  /** Primary line (card title). */
  label: string;
  /** Secondary line under the title. */
  description?: string;
  /**
   * Public URL path to an SVG (e.g. `/icons/foo.svg` from the app `public` folder). Rendered in the
   * right slot; decorative alongside `label` / `description`, so the image uses empty `alt`.
   */
  svg?: string;
  /**
   * Whether to display the radio button vertically.
   */
  vertical?: boolean;
};

type GuidedStepRadioButtonInternalProps = GuidedStepRadioButtonProps & {
  isRow: boolean;
};

const buttonClassNames = {
  root: `${classes.rootShell} ${classes.rootPill}`,
  body: classes.body,
  label: classes.label,
  description: classes.description,
  radio: classes.radio,
  icon: classes.icon,
  inner: classes.innerCollapsed,
};

/**
 * Omits `radio` + `icon` overrides so Mantine renders the themed indicator + checked icon (matches
 * core-ui `Radio`: theme defaults and Mantine default size `sm`).
 */
const buttonClassNamesRow = {
  root: `${classes.rootShell} ${classes.rootPill} ${classes.rootRow}`,
  body: classes.body,
  labelWrapper: classes.labelWrapperFill,
  label: `${classes.label} ${classes.labelFullWidthRow}`,
  description: classes.description,
};

const largeButtonClassNames = {
  root: `${classes.rootShell} ${classes.rootCard}`,
  body: classes.largeBody,
  labelWrapper: classes.labelWrapperFill,
  label: `${classes.label} ${classes.labelFullWidthRow}`,
  description: classes.description,
  inner: classes.innerCollapsed,
  radio: classes.largeRadio,
  icon: classes.icon,
};

/**
 * ### GuidedStepRadioButtonInner
 *
 * @description
 * Shared implementation for default and Row variants.
 *
 * @param props - Internal props including `isRow`
 * @returns Radio option.
 */
const GuidedStepRadioButtonInner = forwardRef<HTMLInputElement, GuidedStepRadioButtonInternalProps>(
  ({ label, icon: Icon, isRow, ...rest }, ref) => {
    const labelNode = (
      <Flex align="center" className={classes.content} justify="flex-start" w="100%">
        {Icon != null ? (
          <Box aria-hidden className={classes.iconSlot}>
            <Icon size={GUIDED_STEP_RADIO_BUTTON_ICON_SIZE} />
          </Box>
        ) : null}
        <Text fw={600} component="span">
          {label}
        </Text>
      </Flex>
    );

    return (
      <MantineRadio
        ref={ref}
        classNames={isRow ? buttonClassNamesRow : buttonClassNames}
        description={undefined}
        label={labelNode}
        labelPosition={isRow ? 'right' : 'left'}
        size={isRow ? 'sm' : 'md'}
        aria-label={label}
        {...rest}
        wrapperProps={
          isRow
            ? {
                ...rest.wrapperProps,
                onClick: (event: ReactMouseEvent<HTMLDivElement>) => {
                  handleGuidedStepRadioRootClick(event);
                  rest.wrapperProps?.onClick?.(event);
                },
              }
            : rest.wrapperProps
        }
      />
    );
  },
);

GuidedStepRadioButtonInner.displayName = 'GuidedStepRadioButtonInner';

const GuidedStepRadioButtonRoot = forwardRef<HTMLInputElement, GuidedStepRadioButtonProps>(
  (props, ref) => <GuidedStepRadioButtonInner {...props} isRow={false} ref={ref} />,
);

GuidedStepRadioButtonRoot.displayName = 'GuidedStepRadioButton';

const GuidedStepRadioButtonRow = forwardRef<HTMLInputElement, GuidedStepRadioButtonProps>(
  (props, ref) => <GuidedStepRadioButtonInner {...props} isRow ref={ref} />,
);

GuidedStepRadioButtonRow.displayName = 'GuidedStepRadioButton.Row';

/**
 * ### GuidedStepRadioButtonLargeInner
 *
 * @description
 * Card-style radio row for guided setup (native circle hidden; border/background show selection).
 *
 * @param props - {@link GuidedStepRadioButtonLargeProps}
 * @returns Radio option row.
 */
const GuidedStepRadioButtonLargeInner = forwardRef<
  HTMLInputElement,
  GuidedStepRadioButtonLargeProps
>(({ label, description, svg, w, vertical, ...rest }, ref) => {
  const hasDescription = description != null && description !== '';
  const descriptionId = useId();

  const labelNode = (
    <Flex
      align="start"
      direction={vertical ? 'column-reverse' : 'row'}
      justify={vertical ? 'flex-start' : 'space-between'}
      gap={4}
      w="100%"
      p="sm"
    >
      <Stack gap={3}>
        <Text size="lg">{label}</Text>
        {hasDescription ? (
          <Text size="sm" id={descriptionId}>
            {description}
          </Text>
        ) : null}
      </Stack>
      {svg != null && svg !== '' ? (
        <Box className={classes.largeIconSlot}>
          <img alt="" className={classes.largeIconSlotImg} src={svg} />
        </Box>
      ) : null}
    </Flex>
  );

  return (
    <MantineRadio
      ref={ref}
      classNames={largeButtonClassNames}
      description={undefined}
      label={labelNode}
      labelPosition="left"
      size="lg"
      w={w ?? '100%'}
      aria-label={label}
      {...(hasDescription ? { 'aria-describedby': descriptionId } : {})}
      {...rest}
    />
  );
});

GuidedStepRadioButtonLargeInner.displayName = 'GuidedStepRadioButton.Large';

type GuidedStepRadioButtonRootType = typeof GuidedStepRadioButtonRoot;
type GuidedStepRadioButtonRowType = typeof GuidedStepRadioButtonRow;
type GuidedStepRadioButtonLargeType = typeof GuidedStepRadioButtonLargeInner;

interface GuidedStepRadioButtonCompound extends GuidedStepRadioButtonRootType {
  /**
   * Row layout: renders the native radio circle on the left (Mantine default indicator, `sm`).
   * Selection is still reflected on the card border/background from shared styles.
   *
   * @example
   * <GuidedStepRadioButtonGroup value={value} onChange={setValue}>
   *   <GuidedStepRadioButton value="a" label="Card style" icon={SomeIcon} />
   *   <GuidedStepRadioButton.Row value="b" label="Row style" icon={OtherIcon} />
   * </GuidedStepRadioButtonGroup>
   */
  Row: GuidedStepRadioButtonRowType;
  /**
   * Large card layout with optional description and trailing SVG.
   *
   * @example
   * <GuidedStepRadioButtonGroup value={value} onChange={setValue}>
   *   <GuidedStepRadioButton.Large value="a" label="Option A" description="More detail" svg="/icons/option-a.svg" />
   * </GuidedStepRadioButtonGroup>
   */
  Large: GuidedStepRadioButtonLargeType;
}

/**
 * ### GuidedStepRadioButton
 *
 * @description
 * Compact radio control for guided setup. By default the native circle is hidden and selection is
 * conveyed via border/background. Use `GuidedStepRadioButton.Row` for a left radio
 * indicator (same visuals as core-ui `Radio`, including default `sm` size). Use
 * `GuidedStepRadioButton.Large` for a card with title, optional description, and optional SVG.
 * Use inside {@link GuidedStepRadioButtonGroup}.
 *
 * @example
 * <GuidedStepRadioButtonGroup value={value} onChange={setValue}>
 *   <GuidedStepRadioButton value="a" label="Option A" icon={SomeIcon} />
 *   <GuidedStepRadioButton.Row value="b" label="Option B" />
 *   <GuidedStepRadioButton.Large value="c" label="Option C" description="Detail" />
 * </GuidedStepRadioButtonGroup>
 *
 * @param props - {@link GuidedStepRadioButtonProps}
 * @returns Radio option.
 */
const GuidedStepRadioButton = Object.assign(GuidedStepRadioButtonRoot, {
  Row: GuidedStepRadioButtonRow,
  Large: GuidedStepRadioButtonLargeInner,
}) as GuidedStepRadioButtonCompound;

export default GuidedStepRadioButton;
